import React, { useState, useRef, useEffect } from 'react'
import { Terminal, ChevronUp, ChevronDown, Loader2, CheckCircle } from 'lucide-react'
import styles from './ShellActivity.module.css'

interface ShellActivityProps {
  activityData: Record<string, any>
  disabled?: boolean
  hideAnswerSection?: boolean
  onExecuteCommand: (command: string) => Promise<{ success: boolean; data?: any; error?: string }>
  onSubmitAnswer: (answer: string) => Promise<{ success: boolean; error?: string }>
}

export function ShellActivity({ activityData, disabled = false, hideAnswerSection = false, onExecuteCommand, onSubmitAnswer }: ShellActivityProps) {
  const [commandInput, setCommandInput] = useState('')
  const [history, setHistory] = useState<string[]>([])
  const [historyIdx, setHistoryIdx] = useState(-1)
  const [output, setOutput] = useState<Array<{ cmd: string; result: string; isError: boolean }>>([])
  const [running, setRunning] = useState(false)
  const [answer, setAnswer] = useState('')
  const [hintOpen, setHintOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const outputRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const data = activityData || {}

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight
    }
  }, [output, running])

  const runCommand = async () => {
    const cmd = commandInput.trim()
    if (!cmd || running) return

    setHistory(prev => [cmd, ...prev])
    setHistoryIdx(-1)
    setCommandInput('')
    setRunning(true)

    try {
      const res = await onExecuteCommand(cmd)
      if (res.success && res.data) {
        const d = res.data
        const combined = [d.stdout, d.stderr].filter((s: string) => s && s.trim()).join('\n')
        const text = combined || d.error || '(no output)'
        setOutput(prev => [...prev, { cmd, result: text, isError: !d.success || !!d.error }])
      } else {
        const errMsg = res.error || 'Command blocked or failed'
        setOutput(prev => [...prev, { cmd, result: errMsg, isError: true }])
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Request failed'
      setOutput(prev => [...prev, { cmd, result: msg, isError: true }])
    } finally {
      setRunning(false)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }

  const navigateHistory = (dir: 'up' | 'down') => {
    if (dir === 'up') {
      const idx = Math.min(historyIdx + 1, history.length - 1)
      setHistoryIdx(idx)
      setCommandInput(history[idx] ?? '')
    } else {
      const idx = Math.max(historyIdx - 1, -1)
      setHistoryIdx(idx)
      setCommandInput(idx === -1 ? '' : history[idx] ?? '')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') runCommand()
    else if (e.key === 'ArrowUp') { e.preventDefault(); navigateHistory('up') }
    else if (e.key === 'ArrowDown') { e.preventDefault(); navigateHistory('down') }
  }

  const clearTerminal = () => setOutput([])

  const handleSubmitAnswer = async () => {
    if (!answer.trim() || submitting) return
    setSubmitError(null)
    setSubmitting(true)
    try {
      const result = await onSubmitAnswer(answer.trim())
      if (result.success) {
        setSubmitted(true)
      } else {
        setSubmitError(result.error || 'Failed to submit')
      }
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to submit — please try again')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted || disabled) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-subtle border border-theme-primary flex items-center justify-center">
          <svg className="w-8 h-8 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-theme-primary mb-2">Waiting for Next Activity</h3>
        <p className="text-theme-text text-sm">Your captain will start the next activity soon.</p>
      </div>
    )
  }

  return (
    <div className={styles.root}>

      {/* Info strip */}
      <div className={styles.infoStrip}>
        <div className={styles.infoContent}>
          <p className={styles.questionText}>
            <Terminal className={styles.infoIcon} />
            {data.question || 'Command Shell'}
          </p>
          {data.target && (
            <div className={styles.targetRow}>
              <span className={styles.targetLabel}>Target</span>
              <span className={styles.targetValue}>
                {data.target}
              </span>
            </div>
          )}
          {data.hints && data.hints.length > 0 && (
            <div className={styles.hintWrapper}>
              <button
                onClick={() => setHintOpen(h => !h)}
                className={styles.hintToggle}
              >
                {hintOpen ? <ChevronUp className={styles.hintChevron} /> : <ChevronDown className={styles.hintChevron} />}
                {hintOpen ? 'Hide hint' : 'Show hint'}
              </button>
              {hintOpen && (
                <p className={styles.hintText}>
                  {data.hints[0]}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Terminal window */}
      <div className={styles.terminal}>

        {/* Title bar */}
        <div className={styles.titleBar}>
          <div className={styles.trafficDots}>
            <div className={styles.dotRed} />
            <div className={styles.dotYellow} />
            <div className={styles.dotGreen} />
          </div>
          <span className={styles.titleText}>
            {data.target ? `investigating: ${data.target}` : 'shell'}
          </span>
          {output.length > 0 && (
            <button
              onClick={clearTerminal}
              className={styles.clearBtn}
            >
              clear
            </button>
          )}
        </div>

        {/* Output */}
        <div
          ref={outputRef}
          className={styles.outputArea}
          onClick={() => inputRef.current?.focus()}
        >
          {output.length === 0 && !running && (
            <p className={styles.emptyPrompt}>
              Type a command below and tap <span className={styles.greenText}>Run</span> to execute it.
            </p>
          )}

          {output.map((entry, i) => (
            <div key={i} className={styles.entryBlock}>
              <div className={styles.commandLine}>
                <span className={styles.promptSymbol}>$</span>
                <span className={styles.commandText}>{entry.cmd}</span>
              </div>
              <pre className={entry.isError ? styles.errorPre : styles.outputPre}>
                {entry.result}
              </pre>
            </div>
          ))}

          {running && (
            <div className={styles.runningIndicator}>
              <Loader2 className={`${styles.spinnerIcon} animate-spin`} />
              <span>running...</span>
            </div>
          )}
        </div>

        {/* History row */}
        {history.length > 0 && (
          <div className={styles.historyRow}>
            <span className={styles.historyLabel}>history:</span>
            {history.slice(0, 5).map((cmd, i) => (
              <button
                key={i}
                onClick={() => { setCommandInput(cmd); setHistoryIdx(i); inputRef.current?.focus() }}
                title={cmd}
                className={styles.historyBtn}
              >
                {cmd}
              </button>
            ))}
          </div>
        )}

        {/* Command input bar */}
        <div className={styles.commandBar}>
          <span className={styles.promptChar}>$</span>
          <input
            ref={inputRef}
            type="text"
            inputMode="text"
            value={commandInput}
            onChange={e => setCommandInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={running}
            placeholder="whois example.com"
            className={styles.commandInput}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />
          <button
            onMouseDown={e => { e.preventDefault(); navigateHistory('up') }}
            disabled={history.length === 0}
            className={styles.navBtn}
            aria-label="Previous command"
          >
            <ChevronUp className={styles.navBtnIcon} />
          </button>
          <button
            onMouseDown={e => { e.preventDefault(); navigateHistory('down') }}
            disabled={historyIdx < 0}
            className={styles.navBtn}
            aria-label="Next command"
          >
            <ChevronDown className={styles.navBtnIcon} />
          </button>
          <button
            onClick={runCommand}
            disabled={!commandInput.trim() || running}
            className={styles.runBtn}
          >
            {running ? (
              <Loader2 className={`${styles.runBtnSpinner} animate-spin`} />
            ) : (
              <span>Run</span>
            )}
          </button>
        </div>
      </div>

      {/* Answer section */}
      {!hideAnswerSection && (
      <div className={styles.answerSection}>
        <div className={styles.answerHeader}>
          <p className={styles.answerTitle}>
            <CheckCircle className={styles.answerTitleIcon} />
            Submit your answer
          </p>
          <p className={styles.answerSubtitle}>
            Use the terminal above to investigate, then type your answer here.
          </p>
        </div>
        <div className={styles.answerBody}>
          <input
            type="text"
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmitAnswer()}
            placeholder="Your answer..."
            className={styles.answerInput}
          />
          {submitError && (
            <p className={styles.submitError}>{submitError}</p>
          )}
          <button
            onClick={handleSubmitAnswer}
            disabled={!answer.trim() || submitting}
            className={styles.submitBtn}
          >
            {submitting ? (
              <span className={styles.submittingInner}>
                <Loader2 className={`${styles.submittingSpinner} animate-spin`} />
                Submitting...
              </span>
            ) : (
              'Submit Answer'
            )}
          </button>
        </div>
      </div>
      )}
    </div>
  )
}
